import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Button, TextField, Chip, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, CircularProgress, Grid, MenuItem, Select,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const CATEGORIES = ['travel', 'supplies', 'food', 'event', 'other'];

const STATUS_CHIP = {
  pending:  { label: 'Pending',  sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  approved: { label: 'Approved', sx: { bgcolor: '#e3f2fd', color: '#1565c0' } },
  rejected: { label: 'Rejected', sx: { bgcolor: '#ffebee', color: '#c62828' } },
  paid:     { label: 'Paid',     sx: { bgcolor: '#e8f5e9', color: '#2e7d32' } },
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = { amount: '', category: 'other', reason: '', expense_date: todayStr(), receipt_url: '' };

export default function TeacherExpenses() {
  const { user, logout } = useAuth();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  const [dialog, setDialog] = useState({ open: false, mode: 'create', expense: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, expense: null });
  const [deleting, setDeleting] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teacherAPI.expenses.list(filterStatus === 'all' ? null : filterStatus);
      setExpenses(res.data.expenses || []);
    } catch {
      showSnack('Failed to load expenses', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialog({ open: true, mode: 'create', expense: null });
  };

  const openEdit = (expense) => {
    setForm({
      amount: expense.amount,
      category: expense.category,
      reason: expense.reason,
      expense_date: expense.expense_date?.slice(0, 10) || todayStr(),
      receipt_url: expense.receipt_url || '',
    });
    setDialog({ open: true, mode: 'edit', expense });
  };

  const closeDialog = () => setDialog({ open: false, mode: 'create', expense: null });

  const handleFormChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { showSnack('Amount must be greater than 0', 'error'); return; }
    if (!form.reason.trim()) { showSnack('Reason is required', 'error'); return; }
    if (!form.expense_date) { showSnack('Expense date is required', 'error'); return; }

    setSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(form.amount),
        category: form.category,
        reason: form.reason.trim(),
        expense_date: form.expense_date,
        receipt_url: form.receipt_url.trim() || null,
      };
      if (dialog.mode === 'create') {
        await teacherAPI.expenses.create(payload);
        showSnack('Expense submitted successfully');
      } else {
        await teacherAPI.expenses.update(dialog.expense.id, payload);
        showSnack('Expense updated');
      }
      closeDialog();
      fetchExpenses();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to save expense', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await teacherAPI.expenses.remove(deleteConfirm.expense.id);
      showSnack('Expense deleted');
      setDeleteConfirm({ open: false, expense: null });
      fetchExpenses();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to delete expense', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = filterStatus === 'all' ? expenses : expenses.filter(e => e.status === filterStatus);

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="bold">My Expense Claims</Typography>
            <Typography variant="body2" color="text.secondary">Submit and track reimbursement requests</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Expense
          </Button>
        </Box>

        {/* Status filter chips */}
        <Box display="flex" gap={1} mb={3} flexWrap="wrap">
          {['all', 'pending', 'approved', 'rejected', 'paid'].map(s => (
            <Chip
              key={s}
              label={s === 'all' ? 'All' : STATUS_CHIP[s]?.label}
              onClick={() => setFilterStatus(s)}
              variant={filterStatus === s ? 'filled' : 'outlined'}
              color={filterStatus === s ? 'primary' : 'default'}
              sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
            />
          ))}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          filterStatus !== 'all' ? (
            <EmptyState
              icon={ReceiptIcon}
              title="No expense claims found"
              description="Try a different status filter."
              variant="no-results"
            />
          ) : (
            <EmptyState
              icon={ReceiptIcon}
              title="No expense claims found"
              actionLabel="Submit your first claim"
              onAction={openCreate}
            />
          )
        ) : (
          <ResponsiveDataView
            rows={filtered}
            keyExtractor={(exp) => exp.id}
            renderCard={(exp) => {
              const cfg = STATUS_CHIP[exp.status] || STATUS_CHIP.pending;
              return (
                <Card variant="outlined" sx={{ mb: 1.5 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }} fontWeight={600}>{exp.category}</Typography>
                      <Chip label={cfg.label} size="small" sx={cfg.sx} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {new Date(exp.expense_date).toLocaleDateString('en-IN')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{exp.reason}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ₹{parseFloat(exp.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                    {exp.review_note && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{exp.review_note}</Typography>
                    )}
                    {exp.status === 'paid' && exp.payment_method && (
                      <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                        via {exp.payment_method}{exp.payment_reference ? ` · ${exp.payment_reference}` : ''}
                      </Typography>
                    )}
                    {exp.status === 'pending' && (
                      <Box display="flex" gap={1} mt={1}>
                        <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(exp)}>Edit</Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => setDeleteConfirm({ open: true, expense: exp })}>Delete</Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            }}
            table={
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                      <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Note</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map(exp => {
                      const cfg = STATUS_CHIP[exp.status] || STATUS_CHIP.pending;
                      return (
                        <TableRow key={exp.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(exp.expense_date).toLocaleDateString('en-IN')}
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{exp.category}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {exp.reason}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {parseFloat(exp.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell>
                            <Chip label={cfg.label} size="small" sx={cfg.sx} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            {exp.review_note && (
                              <Typography variant="caption" color="text.secondary" title={exp.review_note}
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {exp.review_note}
                              </Typography>
                            )}
                            {exp.status === 'paid' && exp.payment_method && (
                              <Typography variant="caption" color="success.main">
                                via {exp.payment_method}{exp.payment_reference ? ` · ${exp.payment_reference}` : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {exp.status === 'pending' && (
                              <Box display="flex" justifyContent="center" gap={0.5}>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => openEdit(exp)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" color="error" onClick={() => setDeleteConfirm({ open: true, expense: exp })}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        )}
      </Container>

      {/* Create / Edit dialog */}
      <MobileFormDialog
        open={dialog.open}
        onClose={closeDialog}
        title={dialog.mode === 'create' ? 'New Expense Claim' : 'Edit Expense Claim'}
        actions={
          <>
            <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : (dialog.mode === 'create' ? 'Submit' : 'Save')}
            </Button>
          </>
        }
      >
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amount (₹)" type="number" fullWidth required
              value={form.amount} onChange={handleFormChange('amount')}
              inputProps={{ min: 0.01, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Expense Date" type="date" fullWidth required
              value={form.expense_date} onChange={handleFormChange('expense_date')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayStr() }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} onChange={handleFormChange('category')} label="Category">
                {CATEGORIES.map(c => (
                  <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Reason" multiline rows={3} fullWidth required
              placeholder="Describe what this expense was for…"
              value={form.reason} onChange={handleFormChange('reason')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Receipt URL (optional)" fullWidth
              placeholder="https://…"
              value={form.receipt_url} onChange={handleFormChange('receipt_url')}
            />
          </Grid>
        </Grid>
      </MobileFormDialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, expense: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Expense</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this ₹{parseFloat(deleteConfirm.expense?.amount || 0).toLocaleString('en-IN')} expense claim?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, expense: null })} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
